import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Col,
  Input,
  List,
  Row,
  Spin,
  Typography,
} from "../utils/antd-imports/AntdImports";
import { ReactComponent as SearchIcon } from "../../../assests/search-icon.svg";
import ProductChatList from "./ProductChatList";
import { useDispatch, useSelector } from "react-redux";
import {
  markCampaignAsRead,
  setIsProductChat,
  setSelectedCampaignProductsChat,
} from "../../../store/slices/common-slice/chat-communication/ChatCommunicationActionItems";
import { clearLiveMsgNotificationForCampaign } from "../../../store/slices/common-slice/web-sockets/WebSocketMsgSlice";
import { capitalizeFirst } from "../utils/Utils";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { getChatHistory } from "../../../store/thunks/common-thunks/commonThunk";

const CampaignChatList = () => {
  const { Text } = Typography;
  const { isProductChat, clearedCampaigns, clearedProducts } = useSelector(
    (state) => state.chatCommunicationHandleSlice
  );
  const isTab1 = useMediaQuery({ maxWidth: 768 });

  const { chatHistory, chatLoading } = useSelector(
    (state) => state.chatHistory
  );
  const {
    isLiveMsgNotification,
    isLiveCampaignMsgCount,
    isLiveProductMsgCount,
  } = useSelector((state) => state.webSocketMsgs);
  // const campaigns = useSelector(state => state.chatCommunicationHandleSlice.campaigns); // assuming campaigns stored here
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedCampaignProducts, setSelectedCampaignProducts] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (isProductChat === true) return;
    dispatch(
      getChatHistory({
        campaignId: null,
        bookingId: null,
      })
    );
  }, [isProductChat]);
  // Helper to format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";

    const now = Date.now();
    const diffMs = now - timestamp;

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} mins ago`;
    if (hours < 24) return `${hours} hrs ago`;
    if (days === 1) return "Yesterday";

    return new Date(timestamp).toLocaleDateString();
  };

  // Merge chat history with campaigns and products
  const mergeCampaignChatData = (campaigns, campaignProductList) => {
    const campaignStatsMap = campaignProductList.reduce((acc, item) => {
      const { campaignId, productId, count, lastMessageTime } = item;

      if (!acc[campaignId]) {
        acc[campaignId] = {
          totalMessageCount: 0,
          lastMessageTime: 0,
          products: {},
        };
      }

      acc[campaignId].totalMessageCount += count;
      acc[campaignId].lastMessageTime = Math.max(
        acc[campaignId].lastMessageTime,
        lastMessageTime
      );

      acc[campaignId].products[productId] = {
        messageCount: count,
        lastMessageTime,
      };

      return acc;
    }, {});

    return campaigns?.map((campaign) => {
      const stats = campaignStatsMap[campaign?.campaignId] || {
        totalMessageCount: 0,
        lastMessageTime: 0,
        products: {},
      };

      const products = (campaign?.products || [])?.map((product) => {
        const productStats = stats?.products[product?.productId] || {
          messageCount: 0,
          lastMessageTime: 0,
        };

        return {
          ...product,
          messageCount: productStats?.messageCount,
          lastMessageTime: productStats?.lastMessageTime,
          lastMessageLabel: productStats?.lastMessageTime
            ? formatRelativeTime(productStats?.lastMessageTime)
            : "",
        };
      });

      return {
        ...campaign,
        totalMessageCount: stats.totalMessageCount,
        lastMessageTime: stats.lastMessageTime,
        lastMessageLabel: stats.lastMessageTime
          ? formatRelativeTime(stats.lastMessageTime)
          : "",
        products,
      };
    });
  };

  // Compute campaign product list with message counts
  const campaignProductList = useMemo(() => {
    const stats = isLiveMsgNotification.reduce((acc, msg) => {
      const { campaignId, productId, timestamp } = msg;

      const clearedAt = clearedCampaigns?.[campaignId];
      if (clearedAt && timestamp <= clearedAt) return acc;

      if (clearedProducts?.[campaignId]?.[productId]) return acc;

      if (!acc[campaignId]) acc[campaignId] = {};
      if (!acc[campaignId][productId]) {
        acc[campaignId][productId] = {
          count: 0,
          lastMessageTime: timestamp,
        };
      }

      acc[campaignId][productId].count += 1;
      acc[campaignId][productId].lastMessageTime = Math.max(
        acc[campaignId][productId].lastMessageTime,
        timestamp
      );

      return acc;
    }, {});

    return Object.entries(stats).flatMap(([campaignId, products]) =>
      Object.entries(products).map(([productId, data]) => ({
        campaignId,
        productId,
        count: data.count,
        lastMessageTime: data.lastMessageTime,
        lastMessageLabel: formatRelativeTime(data.lastMessageTime),
      }))
    );
  }, [isLiveMsgNotification, clearedCampaigns, clearedProducts]);

  // Merge campaigns with computed stats
  const mergedCampaigns = mergeCampaignChatData(
    chatHistory,
    campaignProductList
  );

  // Filter campaigns based on search text
  const filteredCampaigns = useMemo(() => {
    if (!searchText.trim()) return mergedCampaigns;

    const search = searchText.toLowerCase();

    return mergedCampaigns.filter((campaign) => {
      const campaignMatch = campaign.campaignName
        ?.toLowerCase()
        .includes(search);

      const productMatch = campaign.products?.some((product) =>
        product.title?.toLowerCase().includes(search)
      );

      return campaignMatch || productMatch;
    });
  }, [searchText, mergedCampaigns, chatLoading]);

  // Handle campaign click
  const handleCampaignClick = (campaign) => {
    dispatch(
      setSelectedCampaignProductsChat({
        campaignId: campaign.campaignId,
        campaignName: campaign.campaignName,
        products: campaign.products.map((product) => ({
          ...product,
          messageCount: product.messageCount ?? 0,
          lastMessageTime: product.lastMessageTime ?? 0,
        })),
      })
    );
    if (isTab1) {
      // navigate('/seller/')
      navigate(`/seller/products-chat-list/${campaign?.campaignId}`);
    } else {
      dispatch(setIsProductChat(true));
    }
    // dispatch(markCampaignAsRead(campaign.campaignId));
    // 2️⃣ Clear live notifications for this campaign
    // dispatch(clearLiveMsgNotificationForCampaign(campaign.campaignId));
    setSelectedCampaignProducts({
      campaignId: campaign.campaignId,
      campaignName: campaign.campaignName,
      products: campaign.products.map((product) => ({
        ...product,
        messageCount: product.messageCount ?? 0,
        lastMessageTime: product.lastMessageTime ?? 0,
      })),
    });
  };

  return (
    <>
      {isProductChat ? (
        <ProductChatList products={selectedCampaignProducts} />
      ) : (
        <>
          <Input
            className="search-input-chat-campaigns"
            placeholder="Search by Campaign or Product"
            prefix={<SearchIcon />}
            allowClear
            variant="filled"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <List
            itemLayout="horizontal"
            dataSource={filteredCampaigns}
            renderItem={(campaign) => (
              <List.Item
                style={{
                  cursor: "pointer",
                  padding: "16px 12px",
                  background: "#fff",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#FFE6EB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fff")
                }
                onClick={() => handleCampaignClick(campaign)}
              >
                <div style={{ width: "100%" }}>
                  <Row>
                    {" "}
                    <Col span={19}>
                      <Text className="in-app-chat-campaign">
                        {" "}
                        {capitalizeFirst(campaign?.campaignName)}{" "}
                      </Text>
                    </Col>
                    <Col span={5} className="chat-timestamp">
                      <Text style={{ color: "#d2042d" }}>
                        {" "}
                        {campaign?.lastMessageLabel}
                      </Text>{" "}
                    </Col>
                  </Row>
                  <Row>
                    <Col span={22}>
                      <Text className="chat-products-count">
                        {" "}
                        {campaign.products.length} products ·{" "}
                        {campaign?.fromUserId} Buyers{" "}
                      </Text>{" "}
                    </Col>{" "}
                    <Col span={2} className="chat-timestamp">
                      {" "}
                      <Badge
                        count={campaign.totalMessageCount}
                        style={{ backgroundColor: "#d2042d" }}
                      />{" "}
                    </Col>{" "}
                  </Row>
                </div>
              </List.Item>
            )}
          />
          {chatLoading && <Spin fullscreen chatLoading />}
        </>
      )}
    </>
  );
};

export default CampaignChatList;
